<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { generateElementId } from '@oat-sa-private/ui-core';
    import OptionalDropArea from './OptionalDropArea.svelte';
    import DraggableListButton from './DraggableListButton.svelte';

    const dispatch = createEventDispatcher();

    export let key; //key (unique identifier) of component, passed with all fired events, and also to Draggable
    export let draggableGroupKey; //for Draggable; key (unique identifier) shared between Draggables/DropAreas that are allowed to be dragged/dropped to each other
    export let dropArea = false; //whether this item is a DropArea of its own
    export let dropAreaKey = generateElementId('dropArea'); //key (unique identifier) of DropArea, if dropArea=true
    export let removed; //item has been dropped elsewhere and doesn't belong in this list anymore, but we still need it to maintain height
    export let selected; // item has been selected for further action
    export let disabled; //disable component
    export let ariaDescribedBy; //id of html element that contains explanatory text for this item (e.g. keyboard behavior instructions)
    export let ariaGrabbed = false; //aria-grabbed for button
    export let amount = 1; // stack that much amount from this list item
    export let removable = false; // display remove icon
    export let tabbable; //tabindex=0 if true, otherwise -1
    export let placed = false; // apply class of placed item
    export let targetable = false; // on :hover, apply 'targeted' style (both for mouse :hover or drag-over)
    export let notHoverable = false; // on :hover, do not apply normal hover style (both for mouse :hover or drag-over)
    export let minHeight = 0; //allow item be flexible by pointing just minimal height.
    export let minWidth = 0; //allow item be flexible by pointing just minimal width.
    export let width; //exact width. Overwrite 'min-width' style
    export let height; //exact height. Overwrite 'min-height' style
    export let instructionsLang; // test-taker language to read instructions

    let targeted;

    $: style = getStyles(width, height, minHeight, minWidth);

    /**
     * Calculate width and height of item
     * This css variable only for internal usage.
     * @returns {String} styles
     */
    function getStyles() {
        let itemStyles = '';
        itemStyles += width ? `--width: ${width}px;` : '';
        itemStyles += minWidth ? `--min-item-width:${minWidth}px;` : '';
        itemStyles += height ? `--height: ${height}px;` : '';
        itemStyles += minHeight ? `--min-item-height:${minHeight}px;` : '';
        return itemStyles;
    }

    /**
     * Handle 'update' on DropArea
     * @param {CustomEvent} event
     * @fires 'drop' if item is dropped to this area
     */
    function handleDropAreaUpdate(event) {
        if (event.detail.draggableKey !== null) {
            dispatch('drop', event.detail);
            targeted = false;
        }
    }

    /**
     * Handle 'dragOver' on DropArea
     * @param {CustomEvent} event
     * @fires 'dragOver' if item from another area is dragged over this area
     */
    function handleDropAreaOver(event) {
        if (event.detail.dropAreaKey !== event.detail.initialDropAreaKey) {
            targeted = true;
            dispatch('dragOver', event.detail);
        }
    }

    /**
     * Handle 'dragOut' on DropArea
     * @param {CustomEvent} event
     * @fires 'dragOut'
     */
    function handleDropAreaOut(event) {
        dispatch('dragOut', event.detail);
        targeted = false;
    }
</script>

<style>
    li {
        margin: 0;
        position: relative;
        background-color: var(--color-gs-light-alternative-bg);
        border-radius: var(--radius-large);
        min-height: var(--min-item-height, auto);
        min-width: var(--min-item-width, auto);
        height: var(--height, auto);
        width: var(--width, auto);

        &.removed {
            visibility: hidden;
        }
    }

    li > :global(*) {
        /*nested DropArea or nested div */
        width: 100%;
        height: 100%;
    }
</style>

<li
    class="draggable-list-item"
    class:removed
    class:last={amount === 1}
    aria-hidden={removed ? true : void 0}
    style={style ? style : void 0}>
    <OptionalDropArea
        {dropArea}
        {disabled}
        {draggableGroupKey}
        key={dropAreaKey}
        on:update={handleDropAreaUpdate}
        on:dragOver={handleDropAreaOver}
        on:dragOut={handleDropAreaOut}>
        <DraggableListButton
            {key}
            {draggableGroupKey}
            {amount}
            {ariaDescribedBy}
            {disabled}
            {removed}
            {selected}
            {targetable}
            {notHoverable}
            {targeted}
            {placed}
            {removable}
            {tabbable}
            {ariaGrabbed}
            {instructionsLang}
            on:dragStart
            on:dragStop
            on:click
            on:keySelect
            on:remove
            on:keyup
            on:keydown>
            <slot />
            <div slot="remover-aria-label">
                <slot name="remover-aria-label" />
            </div>
        </DraggableListButton>
    </OptionalDropArea>
</li>
